
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
    Calendar, DollarSign, Car, TrendingUp, Clock,
    CheckCircle, XCircle, AlertCircle
} from 'lucide-react'
import { useLanguageStore, useCurrencyStore } from '../../lib/store'
import { formatCurrency } from '../../lib/utils'
import { walletApi } from '../../lib/api'

export default function OwnerDashboard() {
    useTranslation()
    const { language } = useLanguageStore()
    useCurrencyStore()

    const [walletBalance, setWalletBalance] = useState(0)

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await walletApi.getBalance()
                if (response.data?.balance !== undefined) {
                    setWalletBalance(response.data.balance)
                }
            } catch (error) {
                console.error('Failed to fetch wallet:', error)
            }
        }
        fetchData()
    }, [])

    // Stats - wallet from API, others mock for now
    const stats = {
        totalVehicles: 3,
        activeBookings: 2,
        pendingRequests: 1,
        walletBalance: walletBalance,
        thisMonthEarnings: 12500000,
        lastMonthEarnings: 9800000,
        totalEarnings: 63000000,
        pendingPayouts: 3200000,
    }

    const upcomingBookings = [
        { id: 1, customer: 'John Smith', vehicle: 'Honda Air Blade', startDate: '2026-01-08', endDate: '2026-01-10', amount: 400000, status: 'confirmed' },
        { id: 2, customer: 'Sarah Johnson', vehicle: 'Honda Wave Alpha', startDate: '2026-01-09', endDate: '2026-01-11', amount: 300000, status: 'pending' },
    ]

    const recentTransactions = [
        { id: 1, type: 'earning', description: 'Booking #1234 completed', amount: 600000, date: '2026-01-06' },
        { id: 2, type: 'earning', description: 'Booking #1233 completed', amount: 400000, date: '2026-01-05' },
        { id: 3, type: 'withdrawal', description: 'Withdrawal to Vietcombank', amount: -2000000, date: '2026-01-04' },
    ]

    const earningsGrowth = ((stats.thisMonthEarnings - stats.lastMonthEarnings) / stats.lastMonthEarnings * 100).toFixed(0)

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="container mx-auto px-4">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold">{language === 'en' ? 'Owner Dashboard' : 'Bảng Điều Khiển'}</h1>
                    <p className="text-gray-600">{language === 'en' ? 'Manage your rentals and earnings' : 'Quản lý xe cho thuê và thu nhập'}</p>
                </div>

                {/* Wallet Card */}
                <div className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-xl p-6 text-white mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="opacity-80 mb-1">{language === 'en' ? 'Wallet Balance' : 'Số dư ví'}</p>
                            <p className="text-4xl font-bold">{formatCurrency(stats.walletBalance, 'VND')}</p>
                            <p className="text-sm opacity-70 mt-2">
                                {language === 'en' ? 'Pending payouts' : 'Chờ thanh toán'}: {formatCurrency(stats.pendingPayouts, 'VND')}
                            </p>
                        </div>
                        <Link
                            to="/owner/earnings/withdraw"
                            className="px-6 py-3 bg-white text-primary-600 rounded-lg font-bold hover:bg-gray-100 transition"
                        >
                            {language === 'en' ? 'Withdraw' : 'Rút tiền'}
                        </Link>
                    </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <Link to="/owner/vehicles" className="card hover:shadow-lg transition">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                <Car className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-gray-600 text-sm">{language === 'en' ? 'My Vehicles' : 'Xe của tôi'}</p>
                                <p className="text-2xl font-bold">{stats.totalVehicles}</p>
                            </div>
                        </div>
                    </Link>

                    <Link to="/owner/bookings" className="card hover:shadow-lg transition">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                <Calendar className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <p className="text-gray-600 text-sm">{language === 'en' ? 'Active Bookings' : 'Đang thuê'}</p>
                                <p className="text-2xl font-bold text-green-600">{stats.activeBookings}</p>
                            </div>
                        </div>
                    </Link>

                    <Link to="/owner/bookings?status=pending" className="card hover:shadow-lg transition border-l-4 border-orange-500">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                                <Clock className="w-5 h-5 text-orange-600" />
                            </div>
                            <div>
                                <p className="text-gray-600 text-sm">{language === 'en' ? 'Pending' : 'Chờ xác nhận'}</p>
                                <p className="text-2xl font-bold text-orange-600">{stats.pendingRequests}</p>
                            </div>
                        </div>
                    </Link>

                    <div className="card">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                <TrendingUp className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                                <p className="text-gray-600 text-sm">{language === 'en' ? 'This Month' : 'Tháng này'}</p>
                                <p className="text-lg font-bold">{formatCurrency(stats.thisMonthEarnings, 'VND')}</p>
                                <p className={`text-xs ${parseInt(earningsGrowth) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {parseInt(earningsGrowth) >= 0 ? '+' : ''}{earningsGrowth}% vs last month
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Upcoming Bookings */}
                    <div className="card">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold">{language === 'en' ? 'Upcoming Bookings' : 'Đặt Xe Sắp Tới'}</h2>
                            <Link to="/owner/bookings" className="text-primary-600 text-sm font-medium">
                                {language === 'en' ? 'View all' : 'Xem tất cả'}
                            </Link>
                        </div>
                        <div className="space-y-3">
                            {upcomingBookings.map((booking) => (
                                <div key={booking.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                    <div className="flex-1">
                                        <p className="font-medium">{booking.customer}</p>
                                        <p className="text-sm text-gray-600">{booking.vehicle}</p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            {booking.startDate} → {booking.endDate}
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-bold">{formatCurrency(booking.amount, 'VND')}</p>
                                        <span className={`badge ${booking.status === 'confirmed' ? 'badge-success' : 'badge-warning'}`}>
                                            {booking.status}
                                        </span>
                                    </div>
                                    {booking.status === 'pending' && (
                                        <div className="flex gap-2 ml-4">
                                            <button className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600">
                                                <CheckCircle className="w-4 h-4" />
                                            </button>
                                            <button className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600">
                                                <XCircle className="w-4 h-4" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                            {upcomingBookings.length === 0 && (
                                <p className="text-center text-gray-500 py-8">
                                    {language === 'en' ? 'No upcoming bookings' : 'Không có đặt xe sắp tới'}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Recent Transactions */}
                    <div className="card">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold">{language === 'en' ? 'Recent Transactions' : 'Giao Dịch Gần Đây'}</h2>
                            <Link to="/owner/earnings" className="text-primary-600 text-sm font-medium">
                                {language === 'en' ? 'View all' : 'Xem tất cả'}
                            </Link>
                        </div>
                        <div className="space-y-3">
                            {recentTransactions.map((tx) => (
                                <div key={tx.id} className="flex items-center justify-between p-3 border-b last:border-0">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tx.type === 'earning' ? 'bg-green-100' : 'bg-red-100'}`}>
                                            <DollarSign className={`w-5 h-5 ${tx.type === 'earning' ? 'text-green-600' : 'text-red-600'}`} />
                                        </div>
                                        <div>
                                            <p className="font-medium text-sm">{tx.description}</p>
                                            <p className="text-xs text-gray-500">{tx.date}</p>
                                        </div>
                                    </div>
                                    <p className={`font-bold ${tx.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {tx.amount >= 0 ? '+' : ''}{formatCurrency(Math.abs(tx.amount), 'VND')}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
                    <Link to="/owner/vehicles/add" className="card hover:shadow-lg transition text-center py-6">
                        <Car className="w-8 h-8 mx-auto mb-2 text-primary-600" />
                        <p className="font-medium">{language === 'en' ? 'Add Vehicle' : 'Thêm xe'}</p>
                    </Link>
                    <Link to="/owner/calendar" className="card hover:shadow-lg transition text-center py-6">
                        <Calendar className="w-8 h-8 mx-auto mb-2 text-primary-600" />
                        <p className="font-medium">{language === 'en' ? 'Manage Calendar' : 'Lịch cho thuê'}</p>
                    </Link>
                    <Link to="/owner/earnings" className="card hover:shadow-lg transition text-center py-6">
                        <TrendingUp className="w-8 h-8 mx-auto mb-2 text-primary-600" />
                        <p className="font-medium">{language === 'en' ? 'Earnings Report' : 'Báo cáo thu nhập'}</p>
                    </Link>
                    <Link to="/owner/settings" className="card hover:shadow-lg transition text-center py-6">
                        <AlertCircle className="w-8 h-8 mx-auto mb-2 text-primary-600" />
                        <p className="font-medium">{language === 'en' ? 'Settings' : 'Cài đặt'}</p>
                    </Link>
                </div>
            </div>
        </div>
    )
}
